import { CONST } from '@common/constants/const.constant';
import { ESubscriptionStatus } from '@common/enums/subscription-status.enum';
import { IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { consoleService } from '@common/services/console.service';
import { EDeliveryDayCommon, getDayIndexFromDeliveryDay } from '@common/utils/delivery-config/delivery-config.util';
import { getStep, ISubscriptionCommon } from '@common/utils/get-events/get-events.util';
import { isFeeAddon } from '@common/utils/is-fee-addon/is-fee-addon.utils';
import { getNow } from '@common/utils/get-now/now.util';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { apiHooks } from '../hooks/api-hooks';
import { IDeliveryConfiguration, IItemPrice, IOrder, IOrderEstimateResponse, IPause, ISubscription, ISubscriptionItems } from '../item-generated-api';
import { ICachedItemPriceDto } from './cached-item-price.dto';
import { createDeliveryConfigDto, IDeliveryConfigDto } from './delivery-config.dto';
import { createExtendProductCatalogItemDto, IExtendedProductCatalogItemDto } from './extended-product-catalog-item.dto';
import { createOrderDto, IOrderDto } from './order.dto';
import { createPauseDto, IPauseDto } from './pause.dto';
import { IProductCatalogItemDto } from './product-catalog-item.dto';
import { getDiscountString } from '@src/shared/utils/get-discount-string/get-discount-string.util';
import { getDiscountTitle } from '@src/shared/utils/get-discount-title/get-discount-title.util';
import { queryClient } from '@src/shared/query-client';
dayjs.extend(isBetween);

export interface ISubscriptionDto
    extends ISubscriptionCommon,
        Omit<
            ISubscription,
            | 'id'
            | 'box_item'
            | 'addon_items'
            | 'subscription_items'
            | 'shipping_address'
            | 'subscription_with_scheduled_changes'
            | 'start_date'
            | 'started_at'
            | 'status'
        > {
    startDate: Date | undefined;
    endDate: Date;
    nextBillingAtDate: Date | undefined;
    cancelDate: Date | undefined;

    nextDeliveryDate: Date | undefined;
    cutOff: number;

    addons: IExtendedProductCatalogItemDto[];
    feeAddons: IExtendedProductCatalogItemDto[];
    feeAddonsSum: number;
    totalAddonsPrice: number;

    deliveryConfig: IDeliveryConfigDto | undefined;
    shippingAddress: IInputLoqateValue | undefined;
    totalPrice: number;
    cancelled_at?: number | undefined;

    firstPause: IPauseDto | undefined;
    pauses: IPauseDto[] | undefined;

    discount: string | undefined;
    discountTitle: string | undefined;

    temp_debug_only_scheduled_changes: ISubscription | undefined;
    temp_debug_only_subscription: ISubscription | undefined;
    subscriptionBillingDate_blockedOnUI: Date | undefined;

    productObj: IExtendedProductCatalogItemDto | undefined;
    priceObj: ICachedItemPriceDto | undefined;
    lastOrder: IOrderDto | undefined;
    isOrderGeneratedAndBoxSwappedAndDeliveryDayNotReachedYet: boolean | undefined;

    isGlobalAddonLimitExceeded: boolean;
    globalAddonLimit: number;
    status: ESubscriptionStatus;
    shifted_delivery_date: string | undefined;

    isNextBoxSkipped: boolean;
    next_invoice?: IOrderEstimateResponse;
}
interface ISubscriptionDtoProps {
    s1: ISubscription | undefined;
    schedule?: ISubscription;
    delivery_configuration?: IDeliveryConfiguration | undefined;
    last_order?: IOrder | undefined;
    active_pauses?: Array<IPause> | undefined;
    shifted_delivery_date?: string | undefined;
    orderInfo?: IOrderEstimateResponse;
}

export const createSubscriptionDto = ({
    s1,
    schedule,
    delivery_configuration,
    last_order,
    shifted_delivery_date,
    active_pauses,
    orderInfo
}: ISubscriptionDtoProps): ISubscriptionDto => {
    const source = schedule?.id ? (schedule as ISubscription) : (s1 as ISubscription);
    const allProducts = apiHooks.packHouse.getProductsFromCache();

    const deliveryConfig = delivery_configuration ? createDeliveryConfigDto(delivery_configuration) : queryClient.getQueryData(`delivery-config-${source.id}`) as IDeliveryConfigDto | undefined;
    const lastOrder = last_order ? createOrderDto(last_order, deliveryConfig) : queryClient.getQueryData(`last-order-${source.id}`) as IOrderDto | undefined;

    consoleService.assert(lastOrder, 'Missed order');
    consoleService.assert(
        deliveryConfig,
        'Delivery config not found for subscription cf_delivery_configuration_id=' + source.cf_delivery_configuration_id,
    );

    const items = (source.subscription_items || []).sort((s1, s2) =>
        (s1.item_price_id || '') > (s2.item_price_id || '') ? 1 : -1,
    );
    const productItem = items.find((i) => i.item_type === 'plan') as ISubscriptionItems;
    consoleService.assert(productItem, 'product item not found ');

    const allAddons = items
        ?.filter(
            (m) => m.item_type === 'addon' && (m.billing_cycles === undefined || (m.billing_cycles && m.billing_cycles > 0)),
        )
        .map((original) => {
            const product = allProducts?.list.find((p) =>
                p.item?.prices.find((price: IItemPrice) => price.id === original.item_price_id),
            );

            consoleService.assert(product, 'product not found for addon item_price_id=' + original.item_price_id);
            return createExtendProductCatalogItemDto({
                item_price_id: original.item_price_id,
                amount: original.amount,
                quantity: original.quantity,
                product,
            });
        });

    const addons = allAddons.filter((addon) => !isFeeAddon(addon.item_price_id));

    const addonsPrice = addons.map((addon) => addon.amount || 0);
    const totalAddonsPrice = (addonsPrice || []).reduce((sum, curValue) => (sum += curValue), 0);

    const itemPriceId = source?.subscription_items?.find((subsItem) => subsItem.item_type === 'plan')?.item_price_id;
    const startDate =
        source.started_at || source.start_date
            ? new Date(Math.round((source.started_at || source.start_date || 0) * 1000))
            : undefined;
    const step =
        getStep({
            billing_period: source.billing_period || 0,
            billing_period_unit: source.billing_period_unit,
            isNewSubscription: false,
        }) || Number.MAX_VALUE;

    const subscriptionEndDate = source['remaining_billing_cycles']
        ? dayjs(startDate).add(Math.max(source['remaining_billing_cycles'] - 1, 0) * step, 'days')
        : dayjs(new Date(8640000000000000));

    const customDeliveryDate = deliveryConfig?.custom_delivery_date;
    const nextBillingAtDate = source.next_billing_at ? dayjs(source.next_billing_at * 1000) : undefined;
    const nextBillingAtDatePlusDay = dayjs(nextBillingAtDate).add(1, 'day');

    const address = source.shipping_address;
    const cutOff = Number(deliveryConfig?.cutoff_day) || Number.MAX_VALUE;
    const product = allProducts?.list.find((p) =>
        p.item?.prices.find((price: IItemPrice) => price.id === productItem.item_price_id),
    ) as IProductCatalogItemDto;
    consoleService.assert(product, 'product not found by item price id ' + productItem.item_price_id);

    const productObj = createExtendProductCatalogItemDto({
        product,
        amount: productItem.amount,
        item_price_id: productItem.item_price_id,
        quantity: productItem.quantity,
    });

    // configure pauses
    const pauses = active_pauses ? active_pauses?.map((p) => createPauseDto(p, cutOff)) : queryClient.getQueryData(`active-pauses-${source.id}`) as IPauseDto[] | undefined;
    const firstPause = pauses?.length ? pauses[0] : undefined;

    consoleService.assert(pauses, 'Missed pauses');

    const priceObj = product?.item?.prices.find((price) => price.id === itemPriceId);

    const globalAddonLimit = product?.item?.cf_global_limit || 0;
    const isGlobalAddonLimitExceeded = (source.cf_total_relative_volume || 0) >= globalAddonLimit;

    const isOrderGeneratedAndBoxSwappedAndDeliveryDayNotReachedYet =
        lastOrder && dayjs(getNow()).isBefore(lastOrder.finishDeliveryDate) && lastOrder.productObj?.id !== productObj?.id;

    const feeAddons = allAddons.filter((addon) => isFeeAddon(addon.item_price_id));

    // check if next billing date is within the first pause start and end dates
    const isNextBillingSkipped = dayjs(nextBillingAtDate).isBetween(
        firstPause?.startDate,
        firstPause?.resumeBillingDate,
        'd',
        '[]',
    );

    // check if subscription has skip and more than 1 week before next billing date
    const isNextBoxSkipped =
        firstPause && isNextBillingSkipped
            ? dayjs(getNow()).isBefore(dayjs(firstPause?.resumeBillingDate).subtract(1, 'w'), 'd')
            : false;

    return {
        temp_debug_only_scheduled_changes: schedule,
        temp_debug_only_subscription: s1,

        ...source,
        id: source.id || '',
        firstPause,
        pauses,
        cutoffTime: deliveryConfig?.cutOffTime,
        startDate,
        endDate: subscriptionEndDate.toDate(),
        cancelDate: (source as { cancelled_at: number }).cancelled_at
            ? new Date(Math.round((source as { cancelled_at: number }).cancelled_at * 1000))
            : undefined,
        nextBillingAtDate: nextBillingAtDate?.toDate(),
        cutOff,
        deliveryConfig,

        addons,
        feeAddons,
        totalAddonsPrice,
        feeAddonsSum: feeAddons.reduce((res, item) => res + (item.amount || 0), 0),
        shippingAddress: {
            ...address,
            addressLine1: address?.line1 || '',
            addressLine2: address?.line2 || '',
            addressLine3: address?.line3 || '',
            country: address?.country || '',
            city: address?.city || '',
            postCode: address?.zip || '',
            label: [address?.line1, address?.line2, address?.city, address?.zip].filter((f) => f).join(', '),
            originalLabel: [address?.line1, address?.line2, address?.city, address?.zip].filter((f) => f).join(', '),
        },
        discount: getDiscountString(source.coupon),
        discountTitle: getDiscountTitle(source.coupon),
        cutOffDayOfWeekFromDC: dayjs(getNow())
            .set('day', getDayIndexFromDeliveryDay(deliveryConfig?.delivery_day as unknown as EDeliveryDayCommon))
            .subtract(cutOff, 'days')
            .day(),
        nextDeliveryDate:
            customDeliveryDate && dayjs(customDeliveryDate).isAfter(nextBillingAtDatePlusDay)
                ? new Date(customDeliveryDate)
                : nextBillingAtDate
                ? nextBillingAtDate.add(cutOff, 'day').toDate()
                : undefined,

        subscriptionBillingDate_blockedOnUI: nextBillingAtDate
            ? dayjs(nextBillingAtDate).subtract(CONST.BLOCK_UPCOMING_DELIVERY_BEFORE_CUTOFF_IN_MINUTES, 'minutes').toDate()
            : undefined,

        productObj,
        priceObj,
        lastOrder,
        isOrderGeneratedAndBoxSwappedAndDeliveryDayNotReachedYet,
        isGlobalAddonLimitExceeded,
        globalAddonLimit,
        status: source.status as ESubscriptionStatus,
        shifted_delivery_date,
        isNextBoxSkipped,
        totalPrice: orderInfo?.final_amount || 0,
        next_invoice: orderInfo || {}
    };
};
